import { ApiError, fetchWithAuth, getJson, readBody } from "./client";

// Per-month archive inventory. The wizard's StepMonths gates on
// has_rollup === 1 — only months with a rollup are eligible for
// export pre-flight.

export interface InventoryRow {
  content_type: string; // "broadcast" | "podcast" — top-level Hive partition
  year: string;
  month: string;
  n_zips: number;
  n_per_zip_parquets: number | null;
  has_rollup: 0 | 1;
  rollup_schema_version: number | null;
  rollup_size_bytes: number | null;
  rollup_n_rows: number | null;
  rollup_last_built_at: number | null; // epoch ms
}

export interface InventoryResponse {
  inventory: InventoryRow[];
}

// One month, aggregated across the content types a wizard run targets.
// The raw inventory has a row per (content_type, year, month); the
// wizard presents a single tile per month whose totals sum the selected
// corpora that actually have a rollup that month.
export interface MonthTile {
  year: string;
  month: string;
  rollup_n_rows: number;
  rollup_size_bytes: number;
  // Which of the selected content types have a rollup this month —
  // drives the per-(content_type, month) export fan-out and the chips
  // shown on the tile.
  contentTypes: string[];
}

// Collapse the raw per-(content_type, year, month) inventory into one
// tile per month, restricted to rollup-eligible rows of the selected
// content types. A month appears iff at least one selected corpus has a
// rollup for it. Pre-2.0 rows carry no content_type — treat as broadcast.
export function aggregateMonths(
  rows: InventoryRow[],
  contentTypes: string[],
): MonthTile[] {
  const want = new Set(contentTypes);
  const byMonth = new Map<string, MonthTile>();
  for (const r of rows) {
    if (r.has_rollup !== 1) continue;
    const ct = r.content_type ?? "broadcast";
    if (!want.has(ct)) continue;
    const key = `${r.year}-${r.month}`;
    const tile = byMonth.get(key) ?? {
      year: r.year,
      month: r.month,
      rollup_n_rows: 0,
      rollup_size_bytes: 0,
      contentTypes: [],
    };
    tile.rollup_n_rows += r.rollup_n_rows ?? 0;
    tile.rollup_size_bytes += r.rollup_size_bytes ?? 0;
    if (!tile.contentTypes.includes(ct)) tile.contentTypes.push(ct);
    byMonth.set(key, tile);
  }
  return Array.from(byMonth.values());
}

export function getInventory(): Promise<InventoryResponse> {
  return getJson<InventoryResponse>("/api/v1/operator/archive/inventory");
}

export interface RefreshInventoryResponse {
  job_id: string;
  status: string;
}

// Admin-only: kicks off a SCAN job that walks the archive bucket and
// posts a fresh inventory snapshot back. The actual scan runs async on
// the operator side; the SPA should re-poll getInventory() afterward
// to surface new rows.
export async function refreshInventory(): Promise<RefreshInventoryResponse> {
  const res = await fetchWithAuth("/api/v1/operator/archive/refresh", {
    method: "POST",
  });
  if (res.status !== 202) {
    throw new ApiError(
      res.status,
      await readBody(res),
      `archive/refresh returned ${res.status}`,
    );
  }
  return (await res.json()) as RefreshInventoryResponse;
}
